import {
  DownloadOutlined,
  FileExcelOutlined,
  MailOutlined,
  MessageOutlined,
  PrinterOutlined,
  DollarOutlined,
  FileTextOutlined,
  CloseOutlined,
  CloseCircleOutlined,
  WalletOutlined,
  AlipayOutlined ,
  FileExclamationOutlined, 
} from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Collapse,
} from "antd";
import { Circle, PDFDownloadLink } from "@react-pdf/renderer";
import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import "./ActivityTicketScreen.scss";
import "./Hotel/HotelTicketScreen.scss";
import { useAuthContext } from "../../common/providers/AuthProvider";
import { getPassengerData } from "../../helpers/PassegerData";
import ApiClient from "../../helpers/ApiClient";

// import { downloadTicketPdf } from "../../helpers/TicketUtils";

import ActivitiesDoc from "../PdfDocuments/Activities/ActivitiesDoc";
import BuildPackageDoc from "../PdfDocuments/BuildPackage/BuildPackageDoc";
import ActivitiesInvoiceDoc from "../PdfDocuments/Activities/ActivitiesInvoice";
import BuildPackageInvoiceDoc from "../PdfDocuments/BuildPackage/BuildPackageInvoice";
// import PaymentGateway from "../../helpers/PaymentGateway";
// import { useSytContext } from "../../common/providers/SytProvider";
import PayGateway from "../../helpers/PayGateway";
import { useCurrencyContext } from "../providers/CurrencyProvider";
import PayMentSelection from "../PaymentSelection/PaymentSelection";
import { GlobalStatesContext } from "../providers";
import QRCodeLib from "qrcode";

const { Panel } = Collapse;

// Reuse Hotel modal close-icon styling for consistent header alignment.
const activityModalCloseIcon = (
  <CloseOutlined className="hotel-ticket-modal__close-icon" />
);

const ActivityDownloadPdfButton = React.memo(function ActivityDownloadPdfButton({
  label,
}) {
  return (
    <Button className="mb-1 activity-ticket-download-btn">{label}</Button>
  );
});

const getActivityDownloadHeaderLabel = (transferName, bookingStatus) => {
  const status = Number(bookingStatus);
  if (transferName === "Private Transfers") {
    if (status === 11) return "Download Vouchered Transfer";
    return "Download Confirmed Transfer";
  }
  return "Download Vouchered";
};

const isActivitySidebarHidden = (ticketData) => {
  const status = Number(ticketData?.TourDetails?.[0]?.bookingStatus);
  return status === 2 || status === 6;
};

const areActivityDownloadPanelPropsEqual = (prev, next) =>
  prev.ticketData === next.ticketData &&
  prev.pdfTicketData === next.pdfTicketData &&
  prev.logoImage === next.logoImage &&
  prev.voucherData === next.voucherData &&
  prev.activeCurrency === next.activeCurrency &&
  prev.currencyValue === next.currencyValue &&
  Boolean(prev.agent) === Boolean(next.agent);

const ActivityTicketDownloadPanel = React.memo(function ActivityTicketDownloadPanel({
  ticketData,
  pdfTicketData,
  agent,
  logoImage,
  voucherData,
  activeCurrency,
  currencyValue,
}) {
  const [activeKey, setActiveKey] = React.useState([]);
  const transferName = ticketData?.TourDetails?.[0]?.transferName;
  const bookingStatus = ticketData?.TourDetails?.[0]?.bookingStatus;
  const pdfFileName =
    transferName === "Private Transfers"
      ? "Transfer Ticket.pdf"
      : "Activities Tour Ticket.pdf";
  const headerLabel = getActivityDownloadHeaderLabel(transferName, bookingStatus);
  const ticketPayload = pdfTicketData || ticketData;

  const withFareDocument = useMemo(
    () => (
      <ActivitiesDoc
        ticketData={ticketPayload}
        withFare={true}
        agent={agent}
        Logo={logoImage}
        voucherData={voucherData}
        activeCurrency={activeCurrency}
        currencyValue={currencyValue}
      />
    ),
    [ticketPayload, agent, logoImage, voucherData, activeCurrency, currencyValue],
  );

  const withoutFareDocument = useMemo(
    () => (
      <ActivitiesDoc
        ticketData={ticketPayload}
        withFare={false}
        agent={agent}
        Logo={logoImage}
        voucherData={voucherData}
      />
    ),
    [ticketPayload, agent, logoImage, voucherData],
  );

  return (
    <div className="activity-ticket-sidebar-download">
      <Collapse
        activeKey={activeKey}
        onChange={(keys) =>
          setActiveKey(Array.isArray(keys) ? keys : keys ? [keys] : [])
        }
        animated={false}
        destroyInactivePanel={false}
        expandIcon={() => <DownloadOutlined style={{ color: "#3d96ee" }} />}
        expandIconPosition="right"
      >
        <Panel
          header={<span style={{ color: "#3d96ee" }}>{headerLabel}</span>}
          key="1"
        >
          <div className="trip-type-selector activity-ticket-download-options">
            <PDFDownloadLink document={withFareDocument} fileName={pdfFileName}>
              {() => <ActivityDownloadPdfButton label="With Fare" />}
            </PDFDownloadLink>

            <PDFDownloadLink document={withoutFareDocument} fileName={pdfFileName}>
              {() => <ActivityDownloadPdfButton label="Without Fare" />}
            </PDFDownloadLink>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}, areActivityDownloadPanelPropsEqual);

const ActivityTicketEmailPanel = React.memo(function ActivityTicketEmailPanel({
  onEmailWithFare,
  onEmailWithoutFare,
}) {
  const [activeKey, setActiveKey] = React.useState([]);

  return (
    <div className="activity-ticket-sidebar-email">
      <Collapse
        activeKey={activeKey}
        onChange={(keys) =>
          setActiveKey(Array.isArray(keys) ? keys : keys ? [keys] : [])
        }
        animated={false}
        destroyInactivePanel={false}
        expandIcon={() => <MailOutlined style={{ color: "#3d96ee" }} />}
        expandIconPosition="right"
      >
        <Panel
          header={<span style={{ color: "#3d96ee" }}>Email</span>}
          key="1"
        >
          <div className="trip-type-selector activity-ticket-email-options">
            <Button
              block
              className="activity-ticket-email-btn"
              style={{ marginBottom: 10 }}
              onClick={onEmailWithFare}
            >
              With Fare
            </Button>

            <Button block className="activity-ticket-email-btn" onClick={onEmailWithoutFare}>
              Without Fare
            </Button>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
});

const ActivityTicketInvoicePanel = React.memo(function ActivityTicketInvoicePanel({
  invoiceData,
  logoImage,
}) {
  const invoiceDocument = useMemo(
    () => (
      <ActivitiesInvoiceDoc
        invoiceData={invoiceData}
        logo={logoImage}
        type="invoice"
      />
    ),
    [invoiceData, logoImage],
  );

  return (
    <div
      className="ticket-options activity-ticket-invoice-row"
      style={{ backgroundColor: "#f1f8ff" }}
    >
      <p>
        <PDFDownloadLink document={invoiceDocument} fileName="Activities_Invoice.pdf">
          {() => (
            <>
              <strong style={{ color: "#3d96ee" }}>Invoice</strong>
              <FileTextOutlined style={{ color: "#3d96ee" }} />
            </>
          )}
        </PDFDownloadLink>
      </p>
    </div>
  );
});

const ActivityTicketCancelRow = React.memo(function ActivityTicketCancelRow({
  onCancel,
}) {
  return (
    <div className="ticket-options activity-ticket-cancel-row" style={{ backgroundColor: "#ffdbdb" }}>
      <p onClick={onCancel} style={{ color: "#ef6d71" }}>
        Cancel
        <CloseCircleOutlined />
      </p>
    </div>
  );
});

const ActivityTicketMarkupRow = React.memo(function ActivityTicketMarkupRow({
  onClick,
}) {
  return (
    <div className="ticket-options activity-ticket-markup-row" style={{ backgroundColor: "#f1f8ff" }}>
      <p onClick={onClick} style={{ color: "#2f8fed" }}>
        Add Markup
        <AlipayOutlined />
      </p>
    </div>
  );
});

const ActivityTicketPolicyRow = React.memo(function ActivityTicketPolicyRow({
  showTicketPolicyBottom,
  onToggle,
}) {
  return (
    <div className="ticket-options activity-ticket-policy-row" style={{ backgroundColor: "#f1f8ff" }}>
      <p onClick={onToggle} style={{ color: "#2f8fed" }}>
        {showTicketPolicyBottom
          ? "Hide Cancellation Policy"
          : "View Cancellation Policy"}
        <FileExclamationOutlined />
      </p>
    </div>
  );
});

const ActivityTicketSidebar = ({
  getInvoiceData = () => { },
  printTicket = () => { },
  toggleEmailModal = () => { },
  handleCancel = () => { },
  togglePstMrkModal = () => { },
  submitPostMarkup = () => { },
  submitEmailForm = () => { },
  emailModalVisible,
  invoiceData,
  pstMrkModalVisible,
  setWithFare,
  ticketData,
  type,

  // onCancelTicket,
  // getTicketDetails = () => { },
  // cmsFareRules = {},
  // fareRulesResp = null,
  logoImage,
  toggleVoucheredModal = () => { },
  VoucheredConfirm = () => { },
  VoucheredModalVisible,
  voucherData,
  showTicketPolicyBottom,
  setShowTicketPolicyBottom,
  OnPaymentModeSelection = () => { },
  setValue,
  value,
  IsPaymentGatway,
  blob,
  IsDeposit = false
}) => {
  const {
    isLogin: { agent },
    user,
  } = useAuthContext();
  const {
    state: {
      otherData: { ConvFee, promoData, redeemAmount, transactionFee },
      partPayment,
    },
    getPartPayment,
    RemovePromoConvFee,
  } = useContext(GlobalStatesContext);
  const { activeCurrency, currencyValue } = useCurrencyContext();
  const [smsForm] = Form.useForm();
  const [pstMrkForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [userInvoiceVisible, setUserinvoiceVisible] = useState(false);
  const [loadingSpin, setLoadingSpin] = useState(false);
  const [PaymentOptionEnable, setPaymentOptionEnable] = useState(false);
  const [CancelBtnEnable, setCancelBtnEnable] = useState(true);
  const [limitCount, setLimitCount] = useState({
    smsCount: 0,
    emailCount: 0,
  });
  const [pgData, setPgData] = useState({
    data: {},
    visible: false,
  });
  const [pdfTicketData, setPdfTicketData] = useState(ticketData);

  useEffect(() => {
    if(ticketData.TourDetails.length > 0) {
      let paymentEnable = ticketData.TourDetails.filter(x => x.bookingStatus == 3)
      if(paymentEnable.length > 0) {
        setPaymentOptionEnable(true)
      }
      let cancelBtnEnable = ticketData.TourDetails.filter(x => x.bookingStatus == 6 || x.bookingStatus == 2);
      if(cancelBtnEnable.length == ticketData.TourDetails.length) {
        setCancelBtnEnable(false)
      }
    }
  }, []);

  // Prepare ticket data for PDF: generate barcode images (QR) and normalize tour images
  useEffect(() => {
    // Helper: convert remote image to PNG data URL using a canvas (front-end only)
    const convertImageToDataUrl = (url, size = 200) =>
      new Promise((resolve) => {
        try {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              const canvas = document.createElement("canvas");
              canvas.width = size;
              canvas.height = size;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(img, 0, 0, size, size);
              resolve(canvas.toDataURL("image/png"));
            } catch (e) {
              resolve(null);
            }
          };
          img.onerror = () => resolve(null);
          img.src = url;
        } catch (e) {
          resolve(null);
        }
      });

    const preparePdfTicketData = async () => {
      try {
        if (!ticketData || !ticketData.TourDetails) {
          setPdfTicketData(ticketData);
          return;
        }
        // Deep clone to avoid mutating original ticketData
        const cloned = JSON.parse(JSON.stringify(ticketData));
        const tours = cloned.TourDetails || [];

        await Promise.all(
          tours.map(async (tour) => {
            // 1) Normalize ALL remote tour images into 200x200 PNG data URLs
            //    so any future image format/path works reliably in the PDF.
            if (
              tour.tourImage &&
              typeof tour.tourImage === "string" &&
              !tour.tourImage.startsWith("data:") &&
              (tour.tourImage.startsWith("http://") ||
                tour.tourImage.startsWith("https://"))
            ) {
              const normalizedUrl = tour.tourImage.trim();
              const dataUrl = await convertImageToDataUrl(normalizedUrl, 200);
              if (dataUrl) {
                tour.tourImage = dataUrl;
              }
            }

            // 2) Generate QR code images for each barcode
            if (!tour.ticket || !Array.isArray(tour.ticket)) return;
            await Promise.all(
              tour.ticket.map(async (t) => {
                if (t.barcode && !t.barcodeImage) {
                  try {
                    const dataUrl = await QRCodeLib.toDataURL(t.barcode, {
                      margin: 0,
                    });
                    t.barcodeImage = dataUrl;
                  } catch (e) {
                    // Ignore QR generation errors for individual tickets
                  }
                }
              })
            );
          })
        );
        setPdfTicketData(cloned);
      } catch (e) {
        setPdfTicketData(ticketData);
      }
    };

    preparePdfTicketData();
  }, [ticketData]);


  const blockApiReq = (pgType) => {
    setLoadingSpin(true)
    let reqData = {
      TraceId: ticketData?.TraceId,
      UserId: ticketData?.UserId ?? 1,
      RoleType: ticketData?.RoleType ?? 4,
      membership: user?.Membership ?? 1,
      currency: "AED",
      TourDetails: ticketData?.TourDetails.filter(x => x.bookingStatus === 3 || x.bookingStatus === 7).map((item, i) => ({
        tourId: item.tourId,
        apikey: item.apikey,
        tourName: item.tourName,
        cityTourType: item.serviceType,
        optionId: item.optionId,
        optionName: item.optionName,
        adult: item.pax.adults,
        child: item.pax.childCount,
        infant: item.pax.infantCount,
        tourDate: item.journeyDate,
        timeSlotId: item.timeSlotId,
        startTime: item.startTime,
        transferId: item.transferId,
        transferName: item.transferName,
        adultRate: item.adultRate,
        childRate: item.childRate,
        infantRate: item.infantRate,
        serviceTotal: item.bookingStatus === 3 ||  item.bookingStatus === 7 ? item.serviceTotal : 0,
        cancellationDate: item.cancellationDate,
        tourImage: item.tourImage,
        duration: item.duration,
        tourShortDescription: item.tourShortDescription,
        markup: item.markup ?? 0,
        agentMarkup: item.agentMarkup ?? 0,
      })),
      PartPayment: ticketData.PartPayment,
      Passengers: ticketData.Passengers,
      promoCode: promoData.Code ?? "",
      convienenceId: ConvFee.id ?? 0,
      pgType: pgType,
      commission: ticketData.commission ?? 0,
      agentMarkup: ticketData.agentMarkup ?? 0,
      paymentMethod: transactionFee?.methodType,
      isCouponReedem: redeemAmount.status ?? false,
      totalPrice: ticketData.PartPayment.isPartPayment === true ? ticketData.PartPayment.partPaymentAmount : ticketData?.TourDetails.filter(item => item.bookingStatus === 3 || item.bookingStatus === 7).reduce((total, item) => total + item.serviceTotal, transactionFee.amount ?? 0),
      ReferenceNumber: ticketData?.TourDetails[0].referenceNumber
    };
    ApiClient.post(`activities/tours/vourcherTicket/`, reqData)
      .then((res) => {
        setLoadingSpin(false)
        if (res.statusCode === 200) {
          window.location.href = res.data.payment_link;
        } else {
          message.error("Payment Failed", 4);
        }
      })
      .catch(() => { });
  }

  const handleEmailWithFare = useCallback(() => {
    toggleEmailModal();
    setWithFare(1);
  }, [toggleEmailModal, setWithFare]);

  const handleEmailWithoutFare = useCallback(() => {
    toggleEmailModal();
    setWithFare(0);
  }, [toggleEmailModal, setWithFare]);

  const handleCancelClick = useCallback(() => {
    handleCancel(type);
  }, [handleCancel, type]);

  const handleMarkupClick = useCallback(() => {
    togglePstMrkModal();
  }, [togglePstMrkModal]);

  const handlePolicyToggle = useCallback(() => {
    setShowTicketPolicyBottom((prev) => !prev);
  }, [setShowTicketPolicyBottom]);

  const showActivityInvoice =
    agent &&
    invoiceData?.TourDetails?.length > 0 &&
    invoiceData.TourDetails.every(
      (item) =>
        Number(item?.bookingStatus) === 11 ||
        String(item?.BookingStatus || "").toUpperCase() === "VOUCHERED",
    );

  if (type === "Activities" && isActivitySidebarHidden(ticketData)) {
    return null;
  }

  return (
    <>
    <div className="actionable-buttons activity-ticket-sidebar-actions">
      {type === "Activities" && CancelBtnEnable ? (
        <ActivityTicketDownloadPanel
          ticketData={ticketData}
          pdfTicketData={pdfTicketData}
          agent={agent}
          logoImage={logoImage}
          voucherData={voucherData}
          activeCurrency={activeCurrency}
          currencyValue={currencyValue}
        />
      ) : null}

      {type === "BuildPackage" && CancelBtnEnable ? (
        <div className="mb-3">
          <Collapse
            accordion
            expandIcon={() => <DownloadOutlined style={{ color: "#3d96ee" }} />}
            expandIconPosition="right"
          >
            <Panel
              header={
                <span style={{ color: "#3d96ee" }}>Download Vouchered</span>
              }
              key="1"
            >
              <div className="trip-type-selector">
                <PDFDownloadLink
                  document={
                    <BuildPackageDoc
                      ticketData={ticketData}
                      withFare={true}
                      agent={agent}
                      Logo={logoImage}
                    />
                  }
                  fileName="PackageTicket.pdf"
                >
                  {({ loading }) =>
                    loading ? (
                      <Button>Loading document... </Button>
                    ) : (
                      <Button className="mb-1">
                        {agent ? "E-Ticket (With Fare)" : "With Fare"}
                      </Button>
                    )
                  }
                </PDFDownloadLink>
                <PDFDownloadLink
                  document={
                    <BuildPackageDoc
                      ticketData={ticketData}
                      withFare={false}
                      agent={agent}
                      Logo={logoImage}
                    />
                  }
                  fileName="PackageTicket.pdf"
                >
                  {({ loading }) =>
                    loading ? (
                      <Button>Loading document... </Button>
                    ) : (
                      <Button>
                        {agent ? "E-Ticket (Without Fare)" : "Without Fare"}
                      </Button>
                    )
                  }
                </PDFDownloadLink>
                {agent ? (
                  <PDFDownloadLink
                    document={
                      <BuildPackageInvoiceDoc
                        invoiceData={invoiceData}
                        logo={logoImage}
                      />
                    }
                    fileName="Package_Invoice.pdf"
                  >
                    {({ loading }) =>
                      loading ? (
                        <Button>Loading document... </Button>
                      ) : (
                        <Button className="invoice-btn">Invoice</Button>
                      )
                    }
                  </PDFDownloadLink>
                ) : null}
              </div>
            </Panel>
          </Collapse>
        </div>
      ) : null}

      {type === "Activities" &&
      (ticketData.TourDetails[0]?.bookingStatus == 11 ||
        ticketData.TourDetails[0]?.bookingStatus == 3) ? (
        <ActivityTicketEmailPanel
          onEmailWithFare={handleEmailWithFare}
          onEmailWithoutFare={handleEmailWithoutFare}
        />
      ) : null}

      {/* Code Commented Of Ravi Due To Unknown Logic BY Karthik --Start Here */}
      {/* {type === "Activities" && CancelBtnEnable
        ? ticketData.cancellable && (
          <div className="ticket-options" style={{backgroundColor:"#ffdbdb"}}>
            <p onClick={() => handleCancel(type)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#ef6d71', marginRight: '5px', paddingLeft: '15px' }}>
              Cancel
              <CloseCircleOutlined />
            </p>
          </div>
        ):(
          <div className="ticket-options" style={{backgroundColor:"#ffdbdb"}}>
            <p onClick={() => handleCancel(type)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#ef6d71', marginRight: '5px', paddingLeft: '15px' }}>
              {ticketData.TourDetails[0].transferName === "Private Transfers" ? "Cancel  Vouchered" : "Cancel Activity"}
              <CloseCircleOutlined />
            </p>
          </div>
        )
        } */}

        {type === "Activities" && ticketData.cancellable && (
          <ActivityTicketCancelRow onCancel={handleCancelClick} />
         )}
         {/* Code Commented Of Ravi Due To Unknown Logic BY Karthik --End Here */}

      {type === "BuildPackage"
        ? ticketData.cancellable && (
          <div className="ticket-options">
            <p onClick={() => handleCancel(type)}>
              <FileExcelOutlined /> Cancel E-Ticket
            </p>
          </div>
        )
        : null}
      {/* Add Code By Karthik 28/April/2023 */}
      {/* {type === "Activities" && ticketData.TourDetails[0]?.bookingStatus == 3 ?
        <>
          <div className="ticket-options">
            <p onClick={() => toggleVoucheredModal()}>
              <DollarOutlined /> Vouchered
            </p>
          </div>
        </>
        : null} */}

      {type === "Activities" && PaymentOptionEnable && ( agent ? (
        <div className="activity-ticket-sidebar-voucher">
        <Collapse
          accordion
          activeKey={pgData.visible ? ["1"] : []}
          onChange={(keys) => {
            const open = Array.isArray(keys) ? keys.includes("1") : keys === "1";
            setPgData((prev) => ({ ...prev, visible: open }));
          }}
          expandIcon={() => <DollarOutlined style={{ color: '#FF0000' }} />}
          expandIconPosition="right"
        >
          <Panel
            header={
              <span style={{ color: '#EE4B2B' }}>
                Generate Voucher
              </span>
            }
            key="1"
          >
            <div className="ticket-options" style={{marginTop:"-17px", marginBottom:"-15px", textAlign:"start", border:"none"}}>  
              {pgData.visible && (
            <PayGateway blockApiReq={blockApiReq} IsDeposit={IsDeposit} loadingSpin={loadingSpin} OnPaymentModeSelection={OnPaymentModeSelection} />
          )}
        </div>
          </Panel>
        </Collapse>
        </div>
      ):" ")}

      {type === "Activities" && pgData.visible && PaymentOptionEnable && IsPaymentGatway && (
        <div className="mb-3">
          <PayMentSelection setValue={setValue} value={value} />
        </div>
      )}

      {showActivityInvoice && type === "Activities" ? (
        <ActivityTicketInvoicePanel
          invoiceData={invoiceData}
          logoImage={logoImage}
        />
      ) : null}
      {/* Add Code By Karthik 28/April/2023 */}

      {agent ? (
        <>
          <ActivityTicketMarkupRow onClick={handleMarkupClick} />

          <ActivityTicketPolicyRow
            showTicketPolicyBottom={showTicketPolicyBottom}
            onToggle={handlePolicyToggle}
          />
        </>
      ) : null}
    </div>

      {agent ? (
        <>
          <Modal
            title="Add Markup"
            width={250}
            className="promo-modal-header hotel-ticket-modal"
            closeIcon={activityModalCloseIcon}
            visible={pstMrkModalVisible}
            onOk={togglePstMrkModal}
            onCancel={togglePstMrkModal}
            footer={[
              <div key="markup-footer">
                <Button key="close" onClick={togglePstMrkModal}>
                  Cancel
                </Button>

                <Button
                  key="add"
                  type="primary"
                  htmlType="submit"
                  onClick={pstMrkForm.submit}
                >
                  Add
                </Button>
              </div>,
            ]}
          >
            <Form
              form={pstMrkForm}
              layout="vertical"
              onFinish={submitPostMarkup}
            >
              <Row>
                <Col span={24}>
                  <Form.Item
                    label="Amount"
                    name="amount"
                    rules={[
                      {
                        required: true,
                        message: "Required",
                      },
                      {
                        validator: (_, value) => {
                          if (Number(value) > 0) {
                            return Promise.resolve();
                          }
                          return Promise.reject("Please Enter valid Amount");
                        },
                      },
                    ]}
                  >
                    <InputNumber style={{ width: "100%" }} type="number" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Modal>

          <Modal
            title="Vouchered"
            width={400}
            className="promo-modal-header hotel-ticket-modal"
            closeIcon={activityModalCloseIcon}
            visible={VoucheredModalVisible}
            onOk={VoucheredConfirm}
            onCancel={toggleVoucheredModal}
            footer={[
              <div key="voucher-footer">
                <Button key="close" onClick={() => toggleVoucheredModal()}>
                  Cancel
                </Button>
                <Button key="add" type="primary" onClick={VoucheredConfirm}>
                  Confirm
                </Button>
              </div>,
            ]}
          >
            <h6 className="text-center">
              <strong>Note :- </strong>This Ticket will be Non Refundable and You
              will get this ticket in 30 Minutes
            </h6>
          </Modal>

          <Modal
            title="Enter The Email Address"
            className="promo-modal-header hotel-ticket-modal"
            closeIcon={activityModalCloseIcon}
            visible={emailModalVisible}
            onOk={toggleEmailModal}
            onCancel={toggleEmailModal}
            footer={[
              <div key="email-footer">
                <Button key="close" onClick={toggleEmailModal}>
                  Cancel
                </Button>

                <Button
                  key="add"
                  type="primary"
                  htmlType="submit"
                  onClick={emailForm.submit}
                >
                  Send
                </Button>
              </div>,
            ]}
          >
            <Form form={emailForm} layout="vertical" onFinish={submitEmailForm}>
              <Row>
                <Col span={24}>
                  <Form.Item
                    label="Email"
                    name="Email"
                    rules={[
                      { required: true, message: "Required!" },
                      { type: "email", message: "Email is not a valid email" },
                    ]}
                  >
                    <Input placeholder="Enter The Email Address" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Modal>
        </>
      ) : null}
    </>
  );
};
export default ActivityTicketSidebar;
